import { useTranslation } from 'react-i18next';
import {
  UI_MARKETS,
  MARKET_NAMES,
  SUB_SEGMENTS,
  SUB_SEGMENT_LABELS,
  type MarketCode,
  type SubSegment,
} from '@hc/shared';
import {
  FilterModalShell,
  CollapsibleFilterRow,
  MultiSelectList,
  summarizeSelection,
  type SelectOption,
} from './filter-modal-kit.js';

// Add-filters modal for the Operating Pressure panel. Uses the shared
// filter-modal-kit so it renders in the exact same visual language as the
// Screener filter (centered dialog, collapsible rows with a value-pill
// summary, vertical checkbox multi-selects). OP's five filters are all
// multi-selects — Countries · Sub-segments · Categories · Severity ·
// Direction — so there are no numeric ranges and no tabs.

export type SuperCategory = 'regulatory' | 'financial' | 'structural';
export type SeverityKey = 'high' | 'medium' | 'low';
export type DirectionKey = 'new' | 'rising' | 'easing';

const CATEGORY_LABEL: Record<SuperCategory, string> = {
  regulatory: 'Regulatory',
  financial: 'Financial',
  structural: 'Structural',
};
const CATEGORY_KEYS: readonly SuperCategory[] = ['regulatory', 'financial', 'structural'];
const SEVERITY_KEYS: readonly SeverityKey[] = ['high', 'medium', 'low'];
const DIRECTION_KEYS: readonly DirectionKey[] = ['rising', 'easing', 'new'];

export interface OpPressureFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  countries: Set<MarketCode>;
  setCountries: (next: Set<MarketCode>) => void;
  /** Quick toggle — pin to user's launch markets. */
  onMyMarkets: () => void;
  /** Quick toggle — clear country filter (any-country passes). */
  onAllEurope: () => void;

  subSegments: Set<SubSegment>;
  setSubSegments: (next: Set<SubSegment>) => void;

  categories: Set<SuperCategory>;
  setCategories: (next: Set<SuperCategory>) => void;

  severities: Set<SeverityKey>;
  setSeverities: (next: Set<SeverityKey>) => void;

  directions: Set<DirectionKey>;
  setDirections: (next: Set<DirectionKey>) => void;

  resultCount: number;
  totalCount: number;
  onClearAll: () => void;
}

export function OpPressureFilterModal({
  open,
  onOpenChange,
  countries,
  setCountries,
  onMyMarkets,
  onAllEurope,
  subSegments,
  setSubSegments,
  categories,
  setCategories,
  severities,
  setSeverities,
  directions,
  setDirections,
  resultCount,
  totalCount,
  onClearAll,
}: OpPressureFilterModalProps) {
  const { t } = useTranslation();
  const myMarketsActive = sameSet(countries, new Set(UI_MARKETS));
  const allEuropeActive = countries.size === 0;

  // Option lists ({value,label}) for each multi-select.
  const countryOpts: SelectOption[] = UI_MARKETS.map((m) => ({ value: m, label: MARKET_NAMES[m] }));
  const subSegmentOpts: SelectOption[] = SUB_SEGMENTS.map((s) => ({
    value: s,
    label: SUB_SEGMENT_LABELS[s],
  }));
  const categoryOpts: SelectOption[] = CATEGORY_KEYS.map((c) => ({
    value: c,
    label: CATEGORY_LABEL[c],
  }));
  const severityOpts: SelectOption[] = SEVERITY_KEYS.map((s) => ({
    value: s,
    label: t(`panels.opPressure.severity.${s}`),
  }));
  const directionOpts: SelectOption[] = DIRECTION_KEYS.map((d) => ({
    value: d,
    label: t(`panels.opPressure.direction.${d}`),
  }));

  // Countries summary keeps OP's preset semantics: "All Europe" (empty),
  // "My markets" (== launch set), else the Saxo "X +N" form.
  const countrySummary = allEuropeActive
    ? t('panels.opPressure.filterModal.allEurope')
    : myMarketsActive
      ? t('panels.opPressure.filters.myMarkets')
      : summarizeSelection(countries as ReadonlySet<string>, (v) => MARKET_NAMES[v as MarketCode]);

  return (
    <FilterModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('panels.opPressure.filterModal.title')}
      resetLabel={t('panels.opPressure.filterModal.reset')}
      doneLabel={t('panels.opPressure.filterModal.done', {
        count: resultCount,
        total: totalCount.toLocaleString('en-GB'),
      })}
      onReset={onClearAll}
    >
      <CollapsibleFilterRow
        label={t('panels.opPressure.filters.countries')}
        summary={countrySummary}
        active={countries.size > 0}
      >
        {/* Preset quick-toggles above the per-country checkbox list. */}
        <div style={{ display: 'flex', gap: 'var(--s-1)', marginBottom: 'var(--s-1)' }}>
          <QuickChip active={myMarketsActive} onClick={onMyMarkets}>
            {t('panels.opPressure.filters.myMarkets')}
          </QuickChip>
          <QuickChip active={allEuropeActive} onClick={onAllEurope}>
            {t('panels.opPressure.filters.allEurope')}
          </QuickChip>
        </div>
        <MultiSelectList
          options={countryOpts}
          selected={countries as ReadonlySet<string>}
          onChange={(next) => setCountries(next as Set<MarketCode>)}
        />
      </CollapsibleFilterRow>

      <CollapsibleFilterRow
        label={t('panels.opPressure.filters.subSegments')}
        summary={summarizeSelection(
          subSegments as ReadonlySet<string>,
          (v) => SUB_SEGMENT_LABELS[v as SubSegment],
        )}
        active={subSegments.size > 0}
      >
        <MultiSelectList
          options={subSegmentOpts}
          selected={subSegments as ReadonlySet<string>}
          onChange={(next) => setSubSegments(next as Set<SubSegment>)}
        />
      </CollapsibleFilterRow>

      <CollapsibleFilterRow
        label={t('panels.opPressure.filters.categories')}
        summary={summarizeSelection(
          categories as ReadonlySet<string>,
          (v) => CATEGORY_LABEL[v as SuperCategory],
        )}
        active={categories.size > 0}
      >
        <MultiSelectList
          options={categoryOpts}
          selected={categories as ReadonlySet<string>}
          onChange={(next) => setCategories(next as Set<SuperCategory>)}
        />
      </CollapsibleFilterRow>

      <CollapsibleFilterRow
        label={t('panels.opPressure.filters.severity')}
        summary={summarizeSelection(severities as ReadonlySet<string>, (v) =>
          t(`panels.opPressure.severity.${v}`),
        )}
        active={severities.size > 0}
      >
        <MultiSelectList
          options={severityOpts}
          selected={severities as ReadonlySet<string>}
          onChange={(next) => setSeverities(next as Set<SeverityKey>)}
        />
      </CollapsibleFilterRow>

      <CollapsibleFilterRow
        label={t('panels.opPressure.filters.direction')}
        summary={summarizeSelection(directions as ReadonlySet<string>, (v) =>
          t(`panels.opPressure.direction.${v}`),
        )}
        active={directions.size > 0}
      >
        <MultiSelectList
          options={directionOpts}
          selected={directions as ReadonlySet<string>}
          onChange={(next) => setDirections(next as Set<DirectionKey>)}
        />
      </CollapsibleFilterRow>
    </FilterModalShell>
  );
}

// ----- helpers --------------------------------------------------------

/** Small preset toggle chip (My markets / All Europe) shown above the
 *  country checkbox list. Matches the accent-tint active style. */
function QuickChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        fontSize: 'var(--t-meta)',
        padding: '2px var(--s-2)',
        border: '1px solid',
        borderColor: active ? 'var(--accent)' : 'var(--hairline)',
        borderRadius: 2,
        background: active ? 'var(--color-accent-100)' : 'var(--surface)',
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function sameSet<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}
